import { useState, type FormEvent } from 'react'
import { AleloButton, AleloInput, AleloLayout } from '@/components/AleloLayout'
import { AleloCaptcha } from '@/components/AleloCaptcha'
import styles from '@/components/AleloLayout.module.css'
import { RangoAlert, RangoInputCode, RangoLink } from '@/components/rango'
import { formatCpf, isValidCpf } from '@/utils/cpf'
import { isValidAccessCode } from '@/utils/authValidation'
import * as loginCpfHint from '@/utils/loginCpfHint'
import { executeSafely } from '@/utils/helpers'
import { useLoginIdManager } from './hooks/useLoginIdManager'
import {
  useMagicLinkStart,
  type MagicLinkChannel,
} from './hooks/useMagicLinkStart'
import locales from './locales/pt-BR.json'

type Mode = 'password' | 'passwordless'

const fmt = (template: string, vars: Record<string, string | number>) =>
  template.replace(/\{(\w+)\}/g, (_, key) =>
    key in vars ? String(vars[key]) : `{${key}}`,
  )

/**
 * S1 — Entrada do Universal Login (Identifier First, `login-id` / `login`):
 * CPF → Continuar, esqueci minha senha, cadastro, captcha e entrada para o
 * acesso sem senha.
 */
export default function LoginIdScreen() {
  const {
    handleContinue,
    handleForgotPassword,
    handleSignup,
    showForgot,
    showSignup,
    error: sdkError,
    captchaConfig,
    captchaReady,
    onCaptchaToken,
  } = useLoginIdManager()
  const passwordless = useMagicLinkStart()
  const [mode, setMode] = useState<Mode>('password')
  // CPF vindo do cadastro (link "Já tenho conta" com CPF já cadastrado).
  const [cpf, setCpf] = useState(() => loginCpfHint.consume())
  const [code, setCode] = useState('')
  const [error, setError] = useState<string>()
  const [pending, setPending] = useState(false)
  const [touched, setTouched] = useState(false)

  // O hook devolve códigos; aqui viram copy (nunca o texto do provedor).
  function messageFor(code: string | undefined) {
    switch (code) {
      case undefined:
        return undefined
      case 'CPF_INVALID':
        return locales.invalidCpf
      case 'CAPTCHA_REQUIRED':
        return locales.captchaRequired
      default:
        return locales.continueFailed
    }
  }

  const cpfInvalid = touched && cpf.length > 0 && !isValidCpf(cpf)
  const fieldError = cpfInvalid ? locales.invalidCpf : undefined
  const formError = error || messageFor(sdkError)
  const busy = pending || passwordless.sending || passwordless.verifying

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setTouched(true)
    setError(undefined)
    if (!isValidCpf(cpf)) {
      setError(locales.invalidCpf)
      return
    }
    setPending(true)
    try {
      await executeSafely(() => handleContinue(cpf))
    } catch (err) {
      const kind = err instanceof Error ? err.message : ''
      if (kind === 'CPF_INVALID') setError(locales.invalidCpf)
      else if (kind === 'CAPTCHA_REQUIRED') setError(locales.captchaRequired)
      else setError(locales.continueFailed)
    } finally {
      setPending(false)
    }
  }

  async function onPasswordlessChannel(channel: MagicLinkChannel) {
    setTouched(true)
    setError(undefined)
    if (!isValidCpf(cpf)) {
      setError(locales.invalidCpf)
      return
    }
    await passwordless.start(cpf, channel)
  }

  async function onResend() {
    if (passwordless.resendDisabled) return
    await passwordless.resend(cpf)
  }

  async function onCodeSubmit(event: FormEvent) {
    event.preventDefault()
    if (!isValidAccessCode(code)) {
      setError(locales.passwordlessCodeInvalid)
      return
    }
    setError(undefined)
    await passwordless.verify(code)
  }

  function backToPassword() {
    setError(undefined)
    setCode('')
    passwordless.reset()
    setMode('password')
  }

  function toPasswordless() {
    setError(undefined)
    setCode('')
    setMode('passwordless')
  }

  // --- S3: confirmação de envio por e-mail ------------------------------
  if (
    mode === 'passwordless' &&
    passwordless.sent &&
    passwordless.channel === 'email'
  ) {
    return (
      <AleloLayout>
        <h1 className={styles.title}>{locales.passwordlessSentTitle}</h1>
        <p className={styles.subtitle}>{locales.passwordlessSentBody}</p>
        {passwordless.destination ? (
          <p className={styles.muted}>
            {fmt(locales.passwordlessDestination, {
              destination: passwordless.destination,
            })}
          </p>
        ) : null}
        <div className={styles.actions}>
          <AleloButton variant="link" onClick={backToPassword}>
            {locales.passwordlessBack}
          </AleloButton>
        </div>
      </AleloLayout>
    )
  }

  // --- S3: código por SMS -----------------------------------------------
  if (
    mode === 'passwordless' &&
    passwordless.sent &&
    passwordless.channel === 'sms'
  ) {
    return (
      <AleloLayout>
        <h1 className={styles.title}>{locales.passwordlessCodeTitle}</h1>
        <p className={styles.subtitle}>{locales.passwordlessCodeDescription}</p>
        <form className={styles.stack} onSubmit={onCodeSubmit} noValidate>
          <RangoInputCode
            label={locales.passwordlessCodeLabel}
            value={code}
            onChange={setCode}
            length={6}
            supportText={locales.passwordlessCodeSupport}
            error={error === locales.passwordlessCodeInvalid ? error : undefined}
            disabled={busy}
            autoFocus
          />
          {passwordless.resendNotice ? (
            <RangoAlert tone="positive">
              {locales.passwordlessResent}
            </RangoAlert>
          ) : null}
          {passwordless.error ? (
            <RangoAlert tone="negative">
              {passwordless.error === 'invalid_code'
                ? locales.passwordlessInvalidCode
                : locales.passwordlessFailed}
            </RangoAlert>
          ) : null}
          <div className={styles.actions}>
            <AleloButton
              type="submit"
              disabled={busy}
              loading={passwordless.verifying}
            >
              {passwordless.verifying
                ? locales.passwordlessVerifying
                : locales.passwordlessVerify}
            </AleloButton>
            <div className={styles.center}>
              <RangoLink
                onClick={() => void onResend()}
                disabled={passwordless.resendDisabled || busy}
              >
                {passwordless.resendSeconds > 0
                  ? fmt(locales.passwordlessResendIn, {
                      n: passwordless.resendSeconds,
                    })
                  : locales.passwordlessResend}
              </RangoLink>
            </div>
          </div>
          <div className={styles.center}>
            <RangoLink onClick={backToPassword}>
              {locales.passwordlessBack}
            </RangoLink>
          </div>
        </form>
      </AleloLayout>
    )
  }

  // --- S3: escolha do canal ---------------------------------------------
  if (mode === 'passwordless') {
    const channelError =
      error ||
      (passwordless.error === 'invalid_code'
        ? locales.passwordlessInvalidCode
        : passwordless.error
          ? locales.passwordlessFailed
          : undefined)
    return (
      <AleloLayout>
        <h1 className={styles.title}>{locales.passwordlessTitle}</h1>
        <p className={styles.subtitle}>{locales.passwordlessDescription}</p>
        <form
          className={styles.stack}
          onSubmit={(event) => {
            event.preventDefault()
            void onPasswordlessChannel('email')
          }}
          noValidate
        >
          <AleloInput
            id="cpf"
            required
            name="username"
            label={locales.cpfLabel}
            supportText={locales.cpfSupport}
            inputMode="numeric"
            autoComplete="username"
            placeholder={locales.cpfPlaceholder}
            value={cpf}
            error={fieldError}
            onChange={(e) => {
              setCpf(formatCpf(e.target.value))
              if (error) setError(undefined)
            }}
            onBlur={() => setTouched(true)}
            maxLength={14}
            disabled={busy}
          />
          {channelError ? (
            <RangoAlert tone="negative">{channelError}</RangoAlert>
          ) : null}
          <div className={styles.actions}>
            <AleloButton type="submit" disabled={busy}>
              {passwordless.sending && passwordless.channel === 'email'
                ? locales.passwordlessSending
                : locales.passwordlessEmail}
            </AleloButton>
            <AleloButton
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => void onPasswordlessChannel('sms')}
            >
              {passwordless.sending && passwordless.channel === 'sms'
                ? locales.passwordlessSending
                : locales.passwordlessSms}
            </AleloButton>
          </div>
          <div className={styles.center}>
            <RangoLink onClick={backToPassword}>
              {locales.passwordlessBack}
            </RangoLink>
          </div>
        </form>
      </AleloLayout>
    )
  }

  // --- S1: login com CPF --------------------------------------------------
  return (
    <AleloLayout>
      <h1 className={styles.title}>{locales.title}</h1>
      <p className={styles.subtitle}>{locales.subtitle}</p>
      <form className={styles.stack} onSubmit={onSubmit} noValidate>
        <AleloInput
          id="cpf"
          name="username"
          label={locales.cpfLabel}
          supportText={locales.cpfSupport}
          inputMode="numeric"
          autoComplete="username webauthn"
          placeholder={locales.cpfPlaceholder}
          value={cpf}
          error={fieldError}
          onChange={(e) => {
            setCpf(formatCpf(e.target.value))
            if (error) setError(undefined)
          }}
          onBlur={() => setTouched(true)}
          maxLength={14}
          disabled={pending}
        />

        <AleloCaptcha config={captchaConfig} onToken={onCaptchaToken} />

        {formError ? <RangoAlert tone="negative">{formError}</RangoAlert> : null}

        <AleloButton
          type="submit"
          disabled={pending || !captchaReady}
          loading={pending}
        >
          {pending ? locales.submitting : locales.submit}
        </AleloButton>

        <div className={styles.centerRow}>
          {showForgot ? (
            <RangoLink onClick={() => void handleForgotPassword()}>
              {locales.forgotPassword}
            </RangoLink>
          ) : (
            <span />
          )}
          <RangoLink onClick={toPasswordless}>
            {locales.accessWithoutPassword}
          </RangoLink>
        </div>

        {showSignup ? (
          <div className={styles.centerRow}>
            <p className={styles.muted}>{locales.noAccount}</p>
            <RangoLink onClick={handleSignup}>{locales.signup}</RangoLink>
          </div>
        ) : null}
      </form>
    </AleloLayout>
  )
}
